import React, { FC } from 'react';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, TableColumnType, TableRowType } from 'src/types/table';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import translate from '@translate';
import { ProductEanType } from 'src/types/product';
import { colors, sizes } from 'src/themes/default';
import { styled } from '@mui/material/styles';
import { TableLink } from 'src/components/TableLink';
import { createEanLabelUrl } from 'src/utils/createEanLink';

const StyledContainer = styled('div')({
    width: '100%',
});

const allColumns: TableColumnType[] = [
    {
        key: 'ean',
        label: 'ean',
        defaultShow: true,
        customRender: true,
    },
    {
        key: 'active',
        label: 'active',
        defaultShow: true,
        customRender: true,
        customStyle: { width: '120px', minWidth: '120px' },
    },
];

/** Nový řádek z UI má záporné ID — ještě není v backendu. Chybějící ID = načteno z API. */
const isUnsavedEan = (row: TableRowType): boolean => Number(row.ID) < 0;

type EansTablePropsType = {
    data?: ProductEanType[];
    onChange: (value: ProductEanType[]) => void;
    disableEdit?: boolean;
    canDeactivateRemove?: boolean;
    productSku?: string;
};

const EansTable: FC<EansTablePropsType> = (props) => {
    const setRowActive = (row: TableRowType, active: boolean) => {
        // Soft-delete / reaktivace — řádek zůstává v tabulce, mění se jen ACTIVE
        props.onChange((props.data || []).map((item) => (item.ean === row.ean ? { ...item, active } : item)));
    };

    const removeUnsavedRow = (row: TableRowType) => {
        // Ještě neuložený EAN (překlep) — úplně pryč z lokálního stavu
        props.onChange((props.data || []).filter((item) => item.ean !== row.ean));
    };

    const customRender: CustomRenderType = (column, row) => {
        if (column.key === 'ean') {
            const ean = String(row.ean || '');
            if (!ean || !row.active) {
                return ean;
            }
            return (
                <TableLink
                    href={createEanLabelUrl(String(props.productSku || ''), ean)}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {ean}
                </TableLink>
            );
        }
        if (column.key === 'active') {
            return (
                <span style={{ color: row.active ? colors.accent_O : colors.alert_O }}>
                    {translate(row.active ? 'utils.boolCodes.yes' : 'utils.boolCodes.no')}
                </span>
            );
        }
    };

    const rowActions = props.disableEdit
        ? undefined
        : [
              {
                  icon: <DeleteOutlineIcon />,
                  label: translate('productDetail.eansTab.removeEan'),
                  onClick: removeUnsavedRow,
                  style: { height: sizes.table_action_with_input_H },
                  // Odebrat smí každý, kdo smí přidávat — i při WMS zámku (jen neuložené)
                  hiddenFce: (row: TableRowType) => !isUnsavedEan(row),
              },
              {
                  icon: <HighlightOffIcon />,
                  label: translate('productDetail.eansTab.deactivateEan'),
                  onClick: (row: TableRowType) => setRowActive(row, false),
                  style: { height: sizes.table_action_with_input_H },
                  hiddenFce: (row: TableRowType) =>
                      isUnsavedEan(row) || !props.canDeactivateRemove || !row.active,
              },
              {
                  icon: <AddCircleIcon />,
                  label: translate('productDetail.eansTab.activateEan'),
                  onClick: (row: TableRowType) => setRowActive(row, true),
                  style: { height: sizes.table_action_with_input_H },
                  hiddenFce: (row: TableRowType) =>
                      isUnsavedEan(row) || !props.canDeactivateRemove || !!row.active,
              },
          ];

    return (
        <StyledContainer data-testid="product-eans-table">
            <TableSimple
                rowIdKey="ean"
                rows={props.data || []}
                columsLabelPrefix="productDetail.eansTab.table"
                allColumns={allColumns}
                initSort={{ column: 'ean', direction: 'ASC' }}
                rowActions={rowActions}
                disableFilter
                customRender={customRender}
                rowSxFce={(row) =>
                    !row.active
                        ? { color: colors.text_T3, '& .MuiTableCell-root': { color: colors.text_T3 } }
                        : {}
                }
                maxHeight={'unset'}
            />
        </StyledContainer>
    );
};

export default EansTable;
